<template>
  <div class="ex-pending-task-banner">
    <div class="ex-pending-task-banner-icon">
      <Icon name="pending_task_video" />
    </div>
    <div class="ex-pending-task-banner-content">
      <div class="ex-pending-task-banner-header">
        <div class="ex-pending-task-banner-header-left">
          <EXTooltip :delay="100" :title="`${assignedUserFirstName} ${assignedUserLastName}`">
            <ExAvatar
              :size="'large'"
              :picture="assignedUserAvatar"
              :firstName="assignedUserFirstName"
              :lastName="assignedUserLastName"
              class="assigned-user-avatar"
            />
          </EXTooltip>
        </div>
        <div class="ex-pending-task-banner-title">
          <span class="ex-pending-task-banner-title-text"
            >Task Assigned to {{ assignedUserFirstName }} {{ assignedUserLastName }} to upload a
            microlearning video for this checkpoint</span
          >
          <span class="ex-pending-task-banner-description"
            >Microlearning videos are short, targeted resources that help employees quickly grasp
            key concepts and best practices. Adding a video to this checkpoint will help employees
            prepare for inspections using Video generated Tutorials and Inspection
            Instructions.</span
          >
        </div>
      </div>
      <div v-if="!readOnly" class="ex-pending-task-banner-footer">
        <div class="ex-pending-task-banner-actions">
          <StandardButton
            type="red"
            :variant="isCancelReassignDisabled ? 'secondary' : 'tertiary'"
            size="xs"
            label="Cancel"
            :class="isCancelReassignDisabled ? '' : 'cancel-btn'"
            :disabled="isCancelReassignDisabled"
            @click="handleCancel"
          />
          <StandardButton
            type="magenta"
            variant="secondary"
            size="xs"
            label="Reassign"
            :disabled="isCancelReassignDisabled"
            @click="handleReassign"
          />
          <EXTooltip
            v-if="!canSendUploadTaskReminder"
            placement="top"
            :title="sendReminderDisabledTooltip"
            :delay="100"
          >
            <span class="send-reminder-tooltip-wrapper">
              <StandardButton
                type="magenta"
                variant="primary"
                size="xs"
                label="Send Reminder"
                :disabled="isSendReminderDisabled"
                :loaderLeft="sendReminderLoading"
                @click="handleSendReminder"
              />
            </span>
          </EXTooltip>
          <StandardButton
            v-else
            type="magenta"
            variant="primary"
            size="xs"
            label="Send Reminder"
            :disabled="isSendReminderDisabled"
            :loaderLeft="sendReminderLoading"
            @click="handleSendReminder"
          />
        </div>
        <div class="ex-pending-task-banner-assignment-info">
          <span class="assigned-by-text">Assigned By</span>
          <Stamp
            :name="`${assignedByFirstName} ${assignedByLastName}`"
            :avatar="assignedByAvatar"
            :date="null"
            class="assigned-by-card"
          />
          <span class="assigned-on-text">on</span>
          <span class="assigned-date-text">{{ formattedDate }}</span>
        </div>
      </div>
      <div class="ex-pending-task-banner-due-date" v-if="dueDate">
        <span class="due-date-text" :class="{ 'due-date-text--overdue': isOverdue }"
          >Due {{ formattedDueDate }}</span
        >
        <span v-if="isOverdue" class="ex-pending-task-banner-overdue-lozenge">Overdue</span>
      </div>
    </div>
  </div>
</template>

<script setup>
import Icon from "@components/icons/Icon.vue";
import StandardButton from "@components/StandardButton/StandardButton.vue";
import ExAvatar from "@components/common/ExAvatar.vue";
import Stamp from "@components/Stamp/Stamp.vue";
import EXTooltip from "@components/tooltip/EXTooltip.vue";
import { computed } from "vue";

// Props definition
const props = defineProps({
  assignedUserFirstName: {
    type: String,
    default: "Eric",
  },
  assignedUserLastName: {
    type: String,
    default: "Kish",
  },
  assignedUserAvatar: {
    type: String,
    default: null,
  },
  assignedByFirstName: {
    type: String,
    default: "Olivia",
  },
  assignedByLastName: {
    type: String,
    default: "Rhye",
  },
  assignedByAvatar: {
    type: String,
    default: null,
  },
  assignedDate: {
    type: [String, Date, Number],
    default: null,
  },
  dueDate: {
    type: [String, Date, Number],
    default: null,
  },
  lockState: {
    type: String,
    default: "unlocked",
    validator: (v) => ["unlocked", "lockedBySelf", "lockedByOther"].includes(v),
  },
  /** Hides Cancel / Reassign / Send Reminder actions (e.g. version history snapshot). */
  readOnly: {
    type: Boolean,
    default: false,
  },
  sendReminderLoading: {
    type: Boolean,
    default: false,
  },
  /**
   * Whether the backend still considers the linked upload task eligible for a reminder.
   * When false the Send Reminder control stays visible but is disabled with an explanatory
   * tooltip (ER-11907). Defaults to true so older callers that never receive the field keep
   * the previous behaviour.
   */
  canSendUploadTaskReminder: {
    type: Boolean,
    default: true,
  },
});

// Emits
const emit = defineEmits(["cancel", "reassign", "send-reminder"]);

const handleCancel = () => {
  emit("cancel");
};

const handleReassign = () => {
  emit("reassign");
};

const handleSendReminder = () => {
  if (props.sendReminderLoading || !props.canSendUploadTaskReminder) return;
  emit("send-reminder");
};

/** Cancel/Reassign only when the current user holds the edit lock. */
const isCancelReassignDisabled = computed(() => props.lockState !== "lockedBySelf");

/** Send Reminder is disabled while a request is in flight or when the task is no longer active. */
const isSendReminderDisabled = computed(
  () => props.sendReminderLoading || !props.canSendUploadTaskReminder
);

/** Tooltip copy shown only when the control is disabled because the task is no longer active. */
const sendReminderDisabledTooltip = computed(() =>
  props.canSendUploadTaskReminder ? "" : "This upload task is no longer active."
);

const formattedDate = computed(() => {
  if (!props.assignedDate) {
    return "1-Jan-2024 12:45";
  }

  const date = new Date(props.assignedDate);

  if (isNaN(date.getTime())) {
    return "1-Jan-2024 12:45";
  }

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  const hours = date.getHours().toString().padStart(2, "0");
  const minutes = date.getMinutes().toString().padStart(2, "0");

  return `${day}-${month}-${year} ${hours}:${minutes}`;
});

const formattedDueDate = computed(() => {
  if (!props.dueDate) return "";

  const date = new Date(props.dueDate);

  if (isNaN(date.getTime())) return "";

  const day = date.getDate();
  const monthNames = [
    "Jan",
    "Feb",
    "Mar",
    "Apr",
    "May",
    "Jun",
    "Jul",
    "Aug",
    "Sep",
    "Oct",
    "Nov",
    "Dec",
  ];
  return `${day}-${monthNames[date.getMonth()]}-${date.getFullYear()}`;
});

/** Overdue when a deadline exists and is before today (banner only shows for PENDING tasks). */
const isOverdue = computed(() => {
  if (!props.dueDate) return false;
  const due = new Date(props.dueDate);
  if (isNaN(due.getTime())) return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  due.setHours(0, 0, 0, 0);
  return due < today;
});
</script>

<style scoped>
.ex-pending-task-banner {
  display: flex;
  background-color: #ffffff;
  border: 1px solid #eaecf0;
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
  border-radius: 12px;
  padding: 16px;
  flex-direction: row;
  align-items: flex-start;
  gap: 16px;
}
.ex-pending-task-banner-icon {
  display: flex;
  width: 48px;
  height: 48px;
  padding: 12px;
  justify-content: center;
  align-items: center;
  border-radius: var(--radius-md, 8px);
  border: 1px solid
    var(
      --Component-colors-Components-Icons-Featured-icons-Modern-featured-icon-modern-border,
      #eaecf0
    );
  background: var(--Secondary, #f2edff);

  /* Shadows/shadow-xs */
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}
.assigned-user-avatar {
  flex-shrink: 0;
}
.ex-pending-task-banner-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
  min-width: 0;
}
.ex-pending-task-banner-header {
  display: flex;
  align-items: center;
  gap: 8px;
}
.ex-pending-task-banner-header-left {
  align-self: flex-start;
}
.ex-pending-task-banner-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
  flex: 1;
  min-width: 0;
}
.ex-pending-task-banner-title-text {
  font-family: Inter;
  font-weight: 700;
  font-size: 16px;
  line-height: 24px;
  color: #344054;
  margin: 0;
}
.ex-pending-task-banner-description {
  font-family: Inter;
  font-weight: 400;
  font-size: 14px;
  line-height: 22px;
  color: #475467;
  margin: 0;
}
.ex-pending-task-banner-footer {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}
.ex-pending-task-banner-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}
.ex-pending-task-banner-assignment-info {
  display: flex;
  align-items: center;
  gap: 8px;
}
.assigned-by-text {
  color: #667085;
  text-align: center;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}
.assigned-by-card {
  display: inline-block;
}
.assigned-on-text {
  color: #667085;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}
.assigned-date-text {
  overflow: hidden;
  color: #344054;
  text-overflow: ellipsis;
  font-family: Inter;
  font-size: 12px;
  font-style: normal;
  font-weight: 500;
  line-height: 18px;
}
.ex-pending-task-banner-due-date {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
}
.due-date-text {
  color: #344054;
  font-family: Inter;
  font-size: 12px;
  font-weight: 500;
  line-height: normal;
  white-space: nowrap;
}
.due-date-text--overdue {
  color: var(--component-colors-utility-error-utility-error-700, #b42318);
}
.ex-pending-task-banner-overdue-lozenge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 2px 8px;
  border-radius: 9999px;
  border: 1px solid var(--component-colors-utility-error-utility-error-200, #fecdca);
  background: var(--component-colors-utility-error-utility-error-50, #fef3f2);
  color: var(--component-colors-utility-error-utility-error-700, #b42318);
  font-family: Inter, sans-serif;
  font-size: 12px;
  font-weight: 500;
  line-height: 18px;
  white-space: nowrap;
}
::v-deep(.assignments-table-wrapper) {
  padding: 0 !important; /* overrides Vuetify scoped style that sets a padding on the table wrapper */
}
::v-deep(.with-employee-eval .eval-avatar) {
  top: 3px !important; /* overrides Vuetify scoped style that sets a top position on the avatar */
}
::v-deep .cancel-btn {
  background-color: transparent !important; /* overrides Vuetify scoped style that sets a background on tertiary buttons */
}
.send-reminder-tooltip-wrapper {
  display: inline-flex;
}
</style>
